import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router';
import { Logo } from '@/design-system/components/Logo';
import { LanguageSwitcher } from '@/design-system/components/LanguageSwitcher';
import { LinkButton } from '@/design-system/components/Button';
import { Icon } from '@/design-system/components/Icon';
import { ThemeToggle } from '@/design-system/components/ThemeToggle';
import { useAuth } from '@/features/auth/context';
import { resolveLandingRoute } from '@/features/access/portals';
import { useTheme } from '@/features/theme/themeContext';
import { useI18n } from '@/i18n/context';
import styles from './PublicLayout.module.css';

/** Primary navigation (desktop bar). Every entry is a working public route. */
export const PRIMARY_NAV = [
  { to: '/vehicles', key: 'vehicles' },
  { to: '/products', key: 'products' },
  { to: '/services', key: 'services' },
  { to: '/centers', key: 'centers' },
  { to: '/story', key: 'story' },
  { to: '/verify', key: 'verify' },
] as const;

/** Extra destinations in the phone/tablet menu. */
export const SECONDARY_NAV = [
  { to: '/authenticity', key: 'authenticity' },
  { to: '/track', key: 'track' },
  { to: '/warranty', key: 'warranty' },
  { to: '/contact', key: 'contact' },
] as const;

/** Pages that open on full-bleed media: the header starts transparent, light type on imagery. */
const MEDIA_HEROES = new Set(['/', '/story']);

export function PublicLayout() {
  const { t } = useI18n();
  const { theme } = useTheme();
  const { status, access } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  // True while a dark band (data-surface="dark": cinematic chapters, photo heroes) sits under the header.
  const [overDark, setOverDark] = useState(false);
  const isLanding = location.pathname === '/';
  const overMedia = ((MEDIA_HEROES.has(location.pathname) && !scrolled) || overDark) && !menuOpen;

  useEffect(() => setMenuOpen(false), [location.pathname]);

  useEffect(() => {
    let frame = 0;
    const measure = () => {
      frame = 0;
      setScrolled(window.scrollY > 24);
      const under = document.elementFromPoint(window.innerWidth / 2, 74);
      setOverDark(!!under?.closest('[data-surface="dark"]'));
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMenuOpen(false);
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const accountHref = status === 'signed_in' ? resolveLandingRoute(access) : '/login';
  const accountLabel = status === 'signed_in' ? t.common.myAccount : t.common.signIn;
  const logoTone = overMedia || theme === 'dark' ? 'onDark' : 'onLight';

  return (
    <div className={styles.shell}>
      <a href="#main" className="skip-link">
        {t.common.skipToContent}
      </a>
      <header className={[styles.header, overMedia ? styles.headerOverMedia : styles.headerSolid, overMedia && scrolled ? styles.headerOverDark : ''].join(' ')}>
        <div className={styles.headerInner}>
          <Link to="/" viewTransition className={styles.brand} aria-label={t.nav.home}>
            <Logo tone={logoTone} height={34} />
          </Link>

          <nav className={styles.nav} aria-label={t.nav.primary}>
            {PRIMARY_NAV.map((item) => (
              <NavLink key={item.to} to={item.to} viewTransition className={({ isActive }) => [styles.navLink, isActive ? styles.active : ''].join(' ')}>
                {t.nav[item.key]}
              </NavLink>
            ))}
          </nav>

          <div className={styles.actions}>
            <span className={styles.desktopOnly}>
              <LanguageSwitcher tone={overMedia ? 'dark' : 'light'} />
            </span>
            <ThemeToggle tone={overMedia ? 'onDark' : 'default'} className={styles.desktopOnly} />
            <Link to={accountHref} className={[styles.accountLink, styles.desktopOnly].join(' ')}>
              <Icon name="user" size={18} />
              <span>{accountLabel}</span>
            </Link>
            <LinkButton to="/book" variant="primary" size="sm">
              {t.nav.book}
            </LinkButton>
            <button
              type="button"
              className={styles.menuButton}
              aria-expanded={menuOpen}
              aria-controls="mobile-menu"
              onClick={() => setMenuOpen((v) => !v)}
            >
              <Icon name={menuOpen ? 'close' : 'menu'} size={22} label={menuOpen ? t.nav.closeMenu : t.nav.menu} />
            </button>
          </div>
        </div>

        <div id="mobile-menu" className={[styles.mobileMenu, menuOpen ? styles.mobileMenuOpen : ''].join(' ')} hidden={!menuOpen}>
          <nav aria-label={t.nav.primary} className={styles.mobileNav}>
            {[...PRIMARY_NAV, ...SECONDARY_NAV].map((item) => (
              <NavLink key={item.to} to={item.to} viewTransition className={styles.mobileLink}>
                {t.nav[item.key]}
                <Icon name="arrow" size={18} />
              </NavLink>
            ))}
            <NavLink to={accountHref} viewTransition className={styles.mobileLink}>
              {accountLabel}
              <Icon name="arrow" size={18} />
            </NavLink>
          </nav>
          <div className={styles.mobileFooter}>
            <LanguageSwitcher />
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main id="main" className={isLanding || location.pathname === '/story' ? styles.mainLanding : styles.main} tabIndex={-1}>
        <Outlet />
      </main>

      <PublicFooter />
    </div>
  );
}

function PublicFooter() {
  const { t, fmt } = useI18n();
  const year = new Date().getFullYear();
  return (
    <footer className={styles.footer}>
      <div className={styles.footerInner}>
        <div className={styles.footerBrand}>
          <Logo tone="onDark" height={40} />
          <p className={styles.footerTagline}>{t.common.tagline}</p>
        </div>
        <nav className={styles.footerCols} aria-label={t.footer.label}>
          <div>
            <p className={styles.footerHeading}>{t.footer.explore}</p>
            <Link to="/vehicles">{t.nav.vehicles}</Link>
            <Link to="/products">{t.nav.products}</Link>
            <Link to="/services">{t.nav.services}</Link>
            <Link to="/centers">{t.nav.centers}</Link>
            <Link to="/book">{t.nav.book}</Link>
          </div>
          <div>
            <p className={styles.footerHeading}>{t.footer.trust}</p>
            <Link to="/story">{t.nav.story}</Link>
            <Link to="/authenticity">{t.nav.authenticity}</Link>
            <Link to="/verify">{t.nav.verify}</Link>
            <Link to="/warranty">{t.nav.warranty}</Link>
          </div>
          <div>
            <p className={styles.footerHeading}>{t.footer.account}</p>
            <Link to="/login">{t.common.signIn}</Link>
            <Link to="/register">{t.common.register}</Link>
            <Link to="/track">{t.nav.track}</Link>
          </div>
          <div>
            <p className={styles.footerHeading}>{t.footer.support}</p>
            <Link to="/contact">{t.nav.contact}</Link>
            <Link to="/privacy">{t.nav.privacy}</Link>
            <Link to="/credits">{t.nav.credits}</Link>
          </div>
        </nav>
      </div>
      <div className={styles.footerBottom}>
        <p>{fmt(t.footer.rights, { year })}</p>
        <LanguageSwitcher tone="dark" />
      </div>
    </footer>
  );
}
